import { type Database } from '@databases/types/database';
import { getEntryIdFromValueId, getFieldIdFromValueId } from '@databases/utils/utils';

import { type OpenThreadContextData } from './context/CommentContext.tsx';
import { type ThreadWithValueId } from './type.ts';

export const threadsForField = (database: Database, fieldId: string) => {
  return Object.keys(database.commentThreads)
    .filter((valueId) => getFieldIdFromValueId(valueId) === fieldId)
    .map((valueId) => database.commentThreads[valueId]);
};

export const fieldHasThreads = (database: Database, fieldId: string) => {
  return threadsForField(database, fieldId).length > 0;
};

/**
 * Given an threadId and comments object, it will
 * return the associated thread object as well as
 * it's entryId and fieldId.
 */
export const getThreadObjectFromId = (
  threadId: string | undefined,
  comments: Record<
    string,
    {
      threadId: string;
      isResolved: boolean;
      commentCount: number;
      versionNumber: number;
    }[]
  >
) => {
  if (threadId) {
    for (const [valueId, threads] of Object.entries(comments)) {
      const thread = threads.find((thread) => thread.threadId === threadId);
      if (thread) {
        return {
          thread,
          entryId: getEntryIdFromValueId(valueId),
          fieldId: getFieldIdFromValueId(valueId)
        };
      }
    }
  }
};

export const handleThreadNavigation = (
  nextThread: ThreadWithValueId,
  nextThreadIndex: number,
  valueId: string,
  openThreadPanel: boolean | undefined,
  setSelectedThreadIndex: (index: number) => void,
  setFocusedCommentThreadId: (threadId: string | undefined) => void,
  setOpenThread: (data: OpenThreadContextData) => void,
  setSelectedThread: (thread: ThreadWithValueId) => void
): void => {
  if (nextThread?.valueId === valueId) {
    setSelectedThreadIndex(nextThreadIndex);
  } else {
    setFocusedCommentThreadId(nextThread?.threadId);
    setOpenThread({
      valueId: nextThread?.valueId,
      create: false,
      panel: openThreadPanel,
      threadId: nextThread?.threadId
    });
  }
  setSelectedThread(nextThread);
};

export const getNextThreadData = (
  currentIndex: number,
  totalThreads: number,
  allThreads: ThreadWithValueId[]
): { nextThreadIndex: number; nextThread: ThreadWithValueId } => {
  const nextThreadIndex = (currentIndex + 1) % totalThreads;
  return { nextThreadIndex: nextThreadIndex, nextThread: allThreads[nextThreadIndex] };
};

export const getPreviousThreadData = (
  currentIndex: number,
  totalThreads: number,
  allThreads: ThreadWithValueId[]
): { previousThreadIndex: number; previousThread: ThreadWithValueId } => {
  const previousThreadIndex = (currentIndex - 1 + totalThreads) % totalThreads;
  return {
    previousThreadIndex: previousThreadIndex,
    previousThread: allThreads[previousThreadIndex]
  };
};
